import { YEAR_FORMATS } from '../../core/services/constant';
import { InvestmentType } from './../../core/enum/investment-type.enum';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { Moment } from 'moment';
import moment from 'moment';
import { MatDatepicker } from '@angular/material/datepicker';
import { MAT_DATE_FORMATS } from '@angular/material/core';
import { PortfolioService } from '../../core/services/api/portfolio.service';
import { ErrorService } from '../../core/services/error.service';
import { PortfolioSummary } from '../../core/models/portfolio/portfolio-summary';
import { finalize, take, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { PortfolioListModel } from '../../core/models/portfolio/portfolio-list.model';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { portfolioFaqLd, ldToList } from '../../../assets/static/json-ld';
import { FaqAccordion } from '../../core/models/learn/faq-accordion';
enum ShareLinkStatus{
  NoSharesFound,
  SharesFound,
  Completed
}
@Component({
    selector: 'app-my-portfolio',
    templateUrl: './my-portfolio.component.html',
    styleUrls: ['./my-portfolio.component.scss'],
    providers: [{ provide: MAT_DATE_FORMATS, useValue: YEAR_FORMATS }],
    standalone: false
})
export class MyPortfolioComponent implements OnInit, OnDestroy {

  ShareLinkStatus = ShareLinkStatus;
  selectedTab: string = 'portfolio';

  portfolioListModel: PortfolioListModel[];
  portfolioSummary: PortfolioSummary;
  linkStatus?: ShareLinkStatus;
  total: number = 0;
  isLoading: boolean = false;
  params = {
    currentPage: 1,
    take: 12,
  };
  selectedYear: Moment = moment();

  InvestmentType: typeof InvestmentType = InvestmentType;

  portfolioFaqLd = portfolioFaqLd;
  pageFaqs: FaqAccordion[] = ldToList(portfolioFaqLd);
  private readonly destroy$ = new Subject<void>();
  shareLinkBanner?: { bannerText: { title: string; bodyText: string }; buttonText: string };

  // lineChartData: ChartDataSets[];
  // lineChartLabels: Label[];
  // lineChartOptions: (ChartOptions & { annotation: any });
  // lineChartColors: Color[];

  // pieChartOptions: ChartOptions;
  // pieChartLabels: Label[];
  // pieChartData: SingleDataSet;
  // pieChartColors: Color[];

  constructor(
    private readonly portfolioService: PortfolioService,
    private readonly errorService: ErrorService,
    private readonly route: ActivatedRoute,
    private readonly translateService: TranslateService
  ) {}

  ngOnInit() {
    // this.initLineChart();
    // this.initPieChart();

    this.getPortfolio();
    this.buildPageFaqs();
    this.translateService.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => this.buildPageFaqs());

    this.route.queryParams.subscribe((params) => {
      this.selectedTab = params.selectedTab == 'history' ? 'history' : 'portfolio';
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Rebuilds portfolioFaqLd's mainEntity text from translations (same JSON-LD shape as the
   * original hardcoded data), then feeds it through the same ldToList() used before —
   * re-run on every language switch since the accordion renders plain strings via
   * [innerHTML] rather than the translate pipe.
   *
   * Uses translate.get() rather than instant(): on a direct page load, initLanguage()'s
   * translate.use() may still be loading the translation file, and instant() would write
   * the raw portfolio.faq.* keys into the FAQ (and its JSON-LD) until onLangChange fires.
   * get() waits for translations to be ready — emitting synchronously once they already are,
   * which is always true by the time onLangChange fires a subsequent switch — so the field
   * initializer's static English ldToList(portfolioFaqLd) stays on screen until the first
   * resolution instead of a flash of untranslated keys. */
  private buildPageFaqs(): void {
    const faqKeys: Array<[string, string]> = [
      ['digitalCertQuestion', 'digitalCertAnswer'],
      ['pendingCounterQuestion', 'pendingCounterAnswer'],
      ['nomineeOwnershipQuestion', 'nomineeOwnershipAnswer'],
      ['tradeOutsidePstxQuestion', 'tradeOutsidePstxAnswer'],
    ];
    const allKeys = faqKeys.flatMap(([q, a]) => [`portfolio.faq.${q}`, `portfolio.faq.${a}`]);

    this.translateService
      .get(allKeys)
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe((translations) => {
        this.portfolioFaqLd = {
          ...portfolioFaqLd,
          mainEntity: faqKeys.map(([questionKey, answerKey]) => ({
            '@type': 'Question',
            name: translations[`portfolio.faq.${questionKey}`],
            acceptedAnswer: {
              '@type': 'Answer',
              text: translations[`portfolio.faq.${answerKey}`],
            },
          })),
        };
        // ldToList() always sets `open: false` on rebuild; carry forward whichever items
        // the user already had expanded so a language switch doesn't collapse them.
        const openIds = new Set(this.pageFaqs.filter((f) => f.open).map((f) => f.id));
        this.pageFaqs = ldToList(this.portfolioFaqLd).map((f) => ({ ...f, open: openIds.has(f.id) }));
      });
  }

  obtainLinkingReminderText(): { bannerText: { title: string; bodyText: string }; buttonText: string } | null {
    const baseKey = 'portfolio.linkSharesHintBanner';
    let bannerKey: string;

    switch (this.linkStatus) {
      case ShareLinkStatus.SharesFound:
        bannerKey = `${baseKey}.sharesFound`;
        break;
      case ShareLinkStatus.Completed:
        bannerKey = `${baseKey}.success`;
        break;
      case ShareLinkStatus.NoSharesFound:
        bannerKey = `${baseKey}.generic`;
        break;
      default:
        return null;
    }

    return {
      bannerText: {
        title: `${bannerKey}.title`,
        bodyText: `${bannerKey}.bodyText`,
      },
      buttonText:
        this.linkStatus === ShareLinkStatus.Completed
          ? `${baseKey}.startTrading`
          : `${baseKey}.completeEKYC`,
    };
  }

  // initLineChart() {
  //     this.lineChartData = [
  //         { data: [6500, 5900, 8000, 8100, 5600, 5500, 4000] },
  //     ];
  //     this.lineChartLabels = ['January', 'February', 'March', 'April', 'May', 'June', 'July'];
  //     this.lineChartOptions = {
  //         responsive: true,
  //         annotation: {},
  //     };
  //     this.lineChartColors = [
  //         {
  //             borderWidth: 2,
  //             borderColor: '#D8D8D8',
  //             backgroundColor: 'transparent',
  //             pointBackgroundColor: '#0E2B43',
  //             pointBorderColor: '#0E2B43',
  //             pointBorderWidth: 6,
  //             pointHoverBackgroundColor: '#C00000',
  //             pointHoverBorderColor: '#C00000',
  //             pointHoverBorderWidth: 6
  //         },
  //     ];
  // }

  // initPieChart() {
  //     this.pieChartOptions = {
  //         responsive: true,
  //         legend: {
  //             position: 'left',
  //         },
  //     };
  //     this.pieChartLabels = ['Mobile App', 'Education ', 'Technology'];
  //     this.pieChartData = [300, 500, 100];
  //     this.pieChartColors = [
  //         {
  //             backgroundColor: ['#DF7F7F', '#C00000', '#F3CCCC'],
  //         },
  //     ];
  // }

  chosenYearHandler(normalizedYear: Moment, datepicker: MatDatepicker<Moment>) {
    const ctrlValue = moment(this.selectedYear);
    ctrlValue.year(moment(normalizedYear).year());
    this.selectedYear = ctrlValue;
    datepicker.close();
  }

  getPortfolio() {
    this.isLoading = true;

    this.portfolioService
      // Fetch the whole portfolio; MySharesComponent pages by company client-side
      // (12 companies/page — FRC-859), so the API page size is decoupled from the pager.
      //
      // FRC-893: read the WAC-based endpoint (/portfolio/detailsGl, FRC-764). The legacy /details
      // response carries no realisedGainLoss field at all, so the gain/loss breakdown could never
      // show a realised figure once the UI-review page (its only caller) was removed. detailsGl
      // also recomputes unrealised G&L off the WAC cost basis rather than the legacy purchased
      // amount.
      .getPortfolioGl(1, 1000)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: (data) => {
          let portfolioList = data.data;

          portfolioList.forEach(({ portfolio }) => {
            const costBasisOrPurchasedAmount = portfolio?.costBasis ?? portfolio?.purchasedAmount;
            portfolio.unrealisedGainLossPercentage = costBasisOrPurchasedAmount
              ? Number(
                  ((portfolio?.unrealisedGainLoss / costBasisOrPurchasedAmount) * 100).toFixed(2),
                )
              : 0;
          });

          this.portfolioListModel = portfolioList;
          this.portfolioSummary = data.summary;
          this.total = data.total;
          if (this.portfolioSummary?.provisional) {
            // Redact card data
            this.portfolioSummary = {
              amountChanges: 0,
              deltaChanges: 0,
              grossInvestment: 0,
              totalMarketValue: 0,
              totalTokenAmount: 0,
              provisional: data.summary.provisional,
              viewedNewlyLinkedShares: data.summary.viewedNewlyLinkedShares
            };
          }
          // 4 states
          // 1. Provisional: Shares found
          // 2. Provisional: No shares found
          // 3. Completed: New share first view
          // 4. Completed: Not new share first view
          this.linkStatus = this.portfolioSummary?.provisional ? 
          this.portfolioListModel.length > 0 ? ShareLinkStatus.SharesFound : ShareLinkStatus.NoSharesFound : 
          this.portfolioSummary?.viewedNewlyLinkedShares ? null : ShareLinkStatus.Completed;
          this.shareLinkBanner = this.obtainLinkingReminderText();

          if (data.summary?.viewedNewlyLinkedShares === false) {
            this.markPortfolioAsRead();
          }
        },
        error: (err) => {
          this.errorService.showError(err);
        },
      });
  }

  private markPortfolioAsRead() {
    this.portfolioService.markPortfolioAsRead().subscribe({
      error: () => {
        // The portfolio is already visible; ignore read-status persistence failures.
      },
    });
  }
}
